import React, { useState, useEffect, useCallback } from "react";
import {
  Plus,
  Calendar,
  Trash2
} from "lucide-react";
import {
  listPaymentSchedules,
  listPaymentSchedulesPortal,
  createPaymentSchedule,
  createPaymentSchedulePortal,
  deletePaymentSchedule,
  deletePaymentSchedulePortal
} from "../../api/fees/feeApi";
import {
  Button,
  Input,
  Select,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  List,
  EmptyState,
  Skeleton
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

const SCHEDULE_TYPE_OPTIONS = [
  { label: "Monthly", value: "MONTHLY" },
  { label: "Quarterly", value: "QUARTERLY" },
  { label: "Annual", value: "ANNUAL" },
  { label: "Custom", value: "CUSTOM" },
];

const emptyForm = {
  academicYearId: "",
  name: "",
  scheduleType: "MONTHLY",
  description: "",
  installments: [{ label: "Installment 1", dueDate: "", percentage: "100", amount: "" }],
};

const PaymentSchedulesPage = ({ isPortal = false }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = isPortal
        ? await listPaymentSchedulesPortal()
        : await listPaymentSchedules();
      setRows(res.data || res || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to load payment schedules.");
    } finally {
      setLoading(false);
    }
  }, [isPortal]);

  useEffect(() => {
    load();
  }, [load]);

  const updateInstallment = (field, value) => {
    const inst = [...form.installments];
    inst[0] = { ...inst[0], [field]: value };
    setForm({ ...form, installments: inst });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        installments: form.installments.map((i) => ({
          label: i.label,
          dueDate: i.dueDate,
          percentage: i.percentage === "" ? null : Number(i.percentage),
          amount: i.amount === "" ? null : Number(i.amount),
        })),
      };
      if (isPortal) await createPaymentSchedulePortal(payload);
      else await createPaymentSchedule(payload);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to create payment schedule.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this payment schedule?")) return;
    try {
      if (isPortal) await deletePaymentSchedulePortal(id);
      else await deletePaymentSchedule(id);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to delete payment schedule.");
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">Payment Schedules</h1>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          Define installment plans and due dates for fee collection.
        </p>
      </div>

      

      {/* Create Form Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold">Create Schedule</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Academic Year ID"
                placeholder="Enter academic year ID"
                required
                value={form.academicYearId}
                onChange={(e) => setForm({ ...form, academicYearId: e.target.value })}
              />
              <Input
                label="Schedule Name"
                placeholder="e.g. Academic year 2025–26 — monthly"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>

            <Select
              label="Schedule Type"
              options={SCHEDULE_TYPE_OPTIONS}
              value={form.scheduleType}
              onChange={(value) => setForm({ ...form, scheduleType: value })}
            />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Input
                label="Installment Label"
                placeholder="e.g. Term 1"
                value={form.installments[0].label}
                onChange={(e) => updateInstallment("label", e.target.value)}
              />
              <Input
                label="Due Date"
                type="date"
                required
                value={form.installments[0].dueDate}
                onChange={(e) => updateInstallment("dueDate", e.target.value)}
              />
              <Input
                label="Percentage"
                type="number"
                placeholder="100"
                value={form.installments[0].percentage}
                onChange={(e) => updateInstallment("percentage", e.target.value)}
              />
            </div>

            <div className="pt-2">
              <Button
                type="submit"
                variant="primary"
                isLoading={submitting}
                leftIcon={<Plus className="h-4 w-4" />}
                className="w-full sm:w-auto"
              >
                Create Schedule
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Schedules List */}
      {loading ? (
        <Card>
          <CardContent className="p-4 space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center justify-between gap-3 py-2 border-b last:border-0 border-neutral-100 dark:border-neutral-800">
                <div className="flex flex-1 items-center gap-2">
                  <Skeleton height={16} width="35%" />
                  <Skeleton height={20} width={64} />
                  <Skeleton height={14} width="20%" />
                </div>
                <Skeleton height={28} width={56} />
              </div>
            ))}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <List
              items={rows.map((r) => ({
                id: r._id,
                content: (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-neutral-900 dark:text-neutral-100">{r.name}</span>
                    <Badge variant="primary" size="sm">
                      {(r.scheduleType || "").replace(/_/g, " ")}
                    </Badge>
                    <span className="text-xs text-neutral-500 dark:text-neutral-400">
                      {r.installments?.length || 0} installment(s)
                    </span>
                  </div>
                ),
                trailing: (
                  <Button
                    variant="ghost"
                    size="xs"
                    className="text-danger-600 hover:bg-danger-50 hover:text-danger-700"
                    onClick={() => handleDelete(r._id)}
                    leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                  >
                    Delete
                  </Button>
                ),
              }))}
              emptyState={
                <EmptyState
                  icon={<Calendar className="h-8 w-8 text-neutral-400 dark:text-neutral-500" />}
                  title="No payment schedules"
                  description="Create a schedule to define installment plans and due dates."
                />
              }
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PaymentSchedulesPage;